/**
 * 제품 API 클라이언트
 * ---------------------------------------------------------------------------
 * 모든 요청에 액세스 토큰을 붙이고, 401 이면 한 번만 다시 받아 재시도한다.
 * 두 번째 401 은 진짜로 세션이 끝난 것이므로 로그인 화면으로 보낸다.
 *
 * 오류 메시지는 서버가 한글로 주므로 그대로 쓴다. 클라이언트에서 다시
 * 만들어 내면 두 곳의 문구가 갈라진다.
 */

import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { ensureAccessToken, forceRefresh, goToPlatformLogin } from './auth';
import { queryClient } from './query-client';

/**
 * 구독 상태 때문에 쓰기가 막힌 경우의 오류 코드.
 *
 * 플랫폼의 판정(entitlement.access.reason)이 그대로 내려온다. 권한 부족
 * (PERMISSION_DENIED)과 구분해야 한다 — 고객이 할 수 있는 조치가 다르다.
 */
const WRITE_BLOCK_CODES = new Set([
  'TRIAL_EXPIRED',
  'NO_SUBSCRIPTION',
  'PAST_DUE',
  'SUSPENDED',
  'WRITE_BLOCKED',
]);

export interface ApiErrorBody {
  code?: string;
  message?: string;
  details?: Record<string, string[]>;
  modules?: string[];
  requestId?: string;
}

export const api = axios.create({
  // vite 프록시를 통해 제품 API 로 간다.
  baseURL: '/api',
  timeout: 20_000,
});

api.interceptors.request.use(async (config) => {
  const token = await ensureAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<ApiErrorBody>) => {
    const config = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;

    if (error.response?.status === 401 && config && !config._retried) {
      config._retried = true;
      const token = await forceRefresh();

      if (!token) {
        goToPlatformLogin();
        return Promise.reject(error);
      }

      config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
      return api.request(config);
    }

    if (error.response?.status === 401) {
      goToPlatformLogin();
    }

    // 구독 상태 때문에 쓰기가 막혔다면 세션을 다시 받는다.
    //
    // 세션은 60초 캐시라 방금 만료된 경우 화면 상단에 아직 경고가 없다. 저장이
    // 거절되었는데 이유가 화면 어디에도 없으면 "저장 버튼이 고장났다" 가 된다.
    if (error.response?.status === 403 && WRITE_BLOCK_CODES.has(error.response.data?.code ?? '')) {
      void queryClient.invalidateQueries({ queryKey: ['session'] });
    }

    return Promise.reject(error);
  },
);

/** 화면에 보여 줄 오류 문구를 뽑는다. */
export function errorMessage(error: unknown, fallback = '요청을 처리하지 못했습니다.'): string {
  if (axios.isAxiosError<ApiErrorBody>(error)) {
    const body = error.response?.data;

    // 필드 오류가 있으면 첫 번째를 보여 준다. 전부 나열하면 읽히지 않는다.
    const firstDetail = body?.details && Object.values(body.details)[0]?.[0];
    if (firstDetail) return firstDetail;

    if (body?.code === 'ENTITLEMENT_REQUIRED') {
      return `${body.message ?? '구독이 필요합니다.'} (${(body.modules ?? []).join(', ')})`;
    }
    if (body?.message) return body.message;
    if (error.code === 'ECONNABORTED') return '응답이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.';
  }
  return fallback;
}
